/**
 * rother3 companion — i18n helpers
 * Loads locale strings from _locales/<lang>/messages.json
 */

let currentLocale = 'en';
let messages = {};

/**
 * Detect browser locale
 * @returns {string} 'en' or 'id'
 */
function detectLocale() {
  const saved = localStorage.getItem('rother3-language');
  if (saved === 'en' || saved === 'id') {return saved;}

  const browserLang = navigator.language || navigator.userLanguage || 'en';
  if (browserLang.startsWith('id')) {return 'id';}
  return 'en';
}

/**
 * Load locale messages
 * @param {string} locale - 'en' or 'id'
 */
export async function loadLocale(locale) {
  currentLocale = locale || detectLocale();
  try {
    const response = await fetch(`_locales/${currentLocale}/messages.json`);
    messages = await response.json();
  } catch (err) {
    console.error(`[rother3] failed to load locale ${currentLocale}:`, err);
    // Fallback to English
    if (currentLocale !== 'en') {
      try {
        const response = await fetch('_locales/en/messages.json');
        messages = await response.json();
        currentLocale = 'en';
      } catch {
        messages = {};
      }
    }
  }
}

/**
 * Get a translated string
 * @param {string} key - message key
 * @param {string} fallback - fallback if key not found
 * @returns {string}
 */
export function t(key, fallback = key) {
  const entry = messages[key];
  if (entry?.message) {return entry.message;}

  /**
   * A missing key must be **loud**, because the default is invisible.
   *
   * `fallback = key` means a key with no translation renders as `detail_title` on the page —
   * a machine name in the place of a label. That is what the deployed site showed on every
   * heading and button, and it is worse than an empty string: it looks like data rather than
   * like a fault, so nobody reports it.
   *
   * The root cause here was a **build** fault, not a lookup fault. `companion/_locales` sat
   * outside the Vite root's asset graph and was silently dropped from the output, so every
   * fetch 404'd, `messages` stayed `{}`, and `t()` returned its own argument for all 47 keys.
   * The build succeeded, the deploy succeeded, and the page was wrong in a way that looked
   * deliberate. `vite.config.ts` now copies the directory, and this warning plus
   * `tests/unit/companion-i18n.test.ts` are what catch it if that ever regresses.
   */
  console.warn(`[rother3] missing translation for "${key}"`);
  return fallback;
}

/**
 * Get current locale
 * @returns {string}
 */
export function getLocale() {
  return currentLocale;
}

/**
 * Initialize i18n (load locale and apply to page)
 */
export async function initI18n() {
  await loadLocale();

  // Apply translations to elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (key) {el.textContent = t(key);}
  });

  // Apply to elements with data-i18n-placeholder
  document.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (key) {el.setAttribute('placeholder', t(key));}
  });
}
